import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Box
} from '@mui/material';
import { ReasonMappingType } from '../../../../services/ConfigurationService/types/centerType';
import { useGetStandardReasonsQuery } from '../../../../services/AccountService';

interface AddEditReasonMappingDialogProps {
  open: boolean;
  onClose: () => void;
  reasonMappings: ReasonMappingType[];
  // Both callbacks carry the whole mapping list to save, never just the row that changed.
  onSave: (savedMappings: ReasonMappingType[]) => void;
  onDelete?: (remainingMappings: ReasonMappingType[]) => void;
  editingMapping?: ReasonMappingType | null;
}

const INITIAL_FORM_DATA: ReasonMappingType = { sourceReason: '', standardReason: '' };

export function AddEditReasonMappingDialog({
  open,
  onClose,
  reasonMappings,
  onSave,
  onDelete,
  editingMapping
}: AddEditReasonMappingDialogProps) {
  const [form, setForm] = useState<ReasonMappingType>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const {
    data, isLoading: isLoadingReasons, isError: isReasonsError, error: reasonsError,
  } = useGetStandardReasonsQuery();

  const isEditMode = !!editingMapping;
  const standardReasons = data ?? [];

  useEffect(() => {
    setForm(editingMapping ?? INITIAL_FORM_DATA);
    setErrors({});
  }, [editingMapping, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prevForm => ({ ...prevForm, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!form.sourceReason.trim()) {
      newErrors.sourceReason = 'Source reason is required';
    }

    if (!form.standardReason) {
      newErrors.standardReason = 'Standard reason is required';
    }

    // The save would refuse this with a 422 after the dialog closed; catch it here instead.
    const collidesWith = (m: ReasonMappingType) =>
      m.sourceReason !== editingMapping?.sourceReason &&
      m.sourceReason.trim().toLowerCase() === form.sourceReason.trim().toLowerCase();

    if (!newErrors.sourceReason && reasonMappings.some(collidesWith)) {
      newErrors.sourceReason = 'This center already maps that source reason';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const mapping: ReasonMappingType = {
      sourceReason: form.sourceReason.trim(),
      standardReason: form.standardReason
    };

    // The center saves its mappings as one list, so hand the caller the whole list to save
    const mappings = isEditMode
      ? reasonMappings.map(m => (m.sourceReason === editingMapping.sourceReason ? mapping : m))
      : [...reasonMappings, mapping];

    onSave(mappings);
    onClose();
  };

  const handleDelete = () => {
    if (editingMapping && onDelete) {
      onDelete(reasonMappings.filter(m => m.sourceReason !== editingMapping.sourceReason));
      onClose();
    }
  };

  const handleClose = () => {
    setForm(INITIAL_FORM_DATA);
    setErrors({});
    onClose();
  };

  const standardReasonHelperText = () => {
    if (isLoadingReasons) return 'Loading standard reasons...';
    if ((reasonsError as { status?: unknown } | undefined)?.status === 403) {
      return 'Choosing a standard reason needs the Revenue Recognition claim.';
    }
    if (isReasonsError) return 'Standard reasons could not be loaded. Try again.';
    return errors.standardReason;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit Reason Mapping' : 'Add Reason Mapping'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <TextField
            label="Source Reason"
            name="sourceReason"
            value={form.sourceReason}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.sourceReason}
            helperText={errors.sourceReason}
          />

          <TextField
            select
            label="Standard Reason"
            name="standardReason"
            value={form.standardReason}
            onChange={handleChange}
            fullWidth
            required
            disabled={isLoadingReasons || isReasonsError}
            error={!!errors.standardReason || isReasonsError}
            helperText={standardReasonHelperText()}
          >
            {standardReasons.map(label => (
              <MenuItem key={label} value={label}>{label}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box>
            {isEditMode && onDelete && (
              <Button onClick={handleDelete} color="error" variant="outlined">
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={isLoadingReasons || isReasonsError}
            >
              {isEditMode ? 'Update' : 'Add'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
